/**
 * 중요 액션 전 점검 상태 확인 훅
 * 출금, 이체 등 금융 관련 액션 전에 사용
 */
import { useCallback } from 'react';
import { useMaintenanceContext } from '../contexts/MaintenanceContext';

interface UseMaintenanceGuardResult {
    /**
     * 중요 액션 실행 전 점검 상태 확인
     * @returns 액션 진행 가능 여부 (true: 진행 가능, false: 점검 중)
     */
    canProceed: () => Promise<boolean>;

    /**
     * 점검 상태 확인 후 액션 실행
     * 점검 중이면 액션을 실행하지 않고 false 반환
     * @param action 실행할 액션
     * @returns 액션 실행 여부
     */
    executeWithGuard: <T>(action: () => Promise<T>) => Promise<{ success: boolean; result?: T }>;
}

/**
 * 중요 액션(출금, 이체 등) 전에 점검 상태를 즉시 확인하는 훅
 *
 * @example
 * const { canProceed, executeWithGuard } = useMaintenanceGuard();
 *
 * // 방법 1: 직접 확인
 * const handleWithdraw = async () => {
 *     if (!await canProceed()) {
 *         alert('시스템 점검 중입니다.');
 *         return;
 *     }
 *     // 출금 로직 실행
 * };
 *
 * // 방법 2: 가드와 함께 실행
 * const handleTransfer = async () => {
 *     const { success, result } = await executeWithGuard(() => transferApi.execute(data));
 *     if (!success) {
 *         alert('시스템 점검 중입니다.');
 *         return;
 *     }
 *     // result 사용
 * };
 */
export const useMaintenanceGuard = (): UseMaintenanceGuardResult => {
    const { checkMaintenanceNow } = useMaintenanceContext();

    /**
     * 액션 진행 가능 여부 확인
     * 서버에서 즉시 점검 상태를 확인
     */
    const canProceed = useCallback(async (): Promise<boolean> => {
        const isMaintenance = await checkMaintenanceNow();
        return !isMaintenance;
    }, [checkMaintenanceNow]);

    /**
     * 점검 확인 후 액션 실행
     */
    const executeWithGuard = useCallback(async <T>(
        action: () => Promise<T>
    ): Promise<{ success: boolean; result?: T }> => {
        const isMaintenance = await checkMaintenanceNow();

        if (isMaintenance) {
            return { success: false };
        }

        const result = await action();
        return { success: true, result };
    }, [checkMaintenanceNow]);

    return {
        canProceed,
        executeWithGuard,
    };
};

export default useMaintenanceGuard;
